'use client';

import ms from 'ms';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useLogger } from '@/analytics';
import { fetchPhoneFromPool } from '@/context/fetchPhoneFromPool';
import { DEFAULT_PHONE, PhoneConfig } from '@/models';

export { DEFAULT_PHONE };

type Options = { enabled?: boolean };

export const useConciergePhone = ({ enabled = true }: Options = {}): string | undefined => {
  const { addError } = useLogger();

  const [phoneConfig, setPhoneConfig] = useState<PhoneConfig>();
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const retryCount = useRef(0);

  const phone = phoneConfig?.phone ?? DEFAULT_PHONE;

  const fetchAvailablePhone = useCallback(async () => {
    try {
      const newPhoneConfig = await fetchPhoneFromPool();
      const { status, lock_exp } = newPhoneConfig || {};

      if (status === true && lock_exp) {
        // based on phoneConfig.lock_exp timestamp refresh the phone number roughly a minute before it expires
        const now = new Date().getTime();
        const exp = new Date(lock_exp).getTime();
        const wait = exp - now - ms('1m');

        clearTimeout(timer.current);
        timer.current = setTimeout(() => fetchAvailablePhone(), wait);
        retryCount.current = 0;

        setPhoneConfig(newPhoneConfig);
      } else {
        // this phone is NOT locked
        setPhoneConfig(newPhoneConfig);
        clearTimeout(timer.current);

        // if NOT available do exponential retry backoff (1 min, 2 min, 4 min ...)
        timer.current = setTimeout(() => fetchAvailablePhone(), ms(`${2 ** retryCount.current}m`));
        retryCount.current += 1;
      }
    } catch (err) {
      addError(err, { action: 'useConciergePhone' });
    }
  }, [addError]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    fetchAvailablePhone();

    const onVisibilityChange = () => {
      if (document.hidden) {
        clearTimeout(timer.current);
      } else {
        fetchAvailablePhone();
      }
    };

    addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      removeEventListener('visibilitychange', onVisibilityChange);
      clearTimeout(timer.current);
    };
  }, [enabled, fetchAvailablePhone]);

  if (!enabled) {
    return undefined;
  }

  return phone;
};
